import { useState, useEffect } from "react";
import JobList from "./JobList";
import JobTypeSelect from "./JobTypeSelect";
import Spinner from '../../Spinner';
import { useApi } from '../../../api/ApiProvider';
import Api from '../../../axiosApi/api';
import { CreateJobCommand, UpdateJobCommand, Job } from "axiosApi/models";
import { useToastMessageQueue } from "components/ToastMessages/ToastMessageProvider";
import {
    Box,
    Grid,
    Typography,
    TextField,
    Checkbox,
    FormControlLabel,
    Button
  } from '@mui/material';
import { useDialog } from 'common/DialogProvider';
import TaskJobModal from "./Task/TaskJobModal";
import { PermissionsGate } from "components/authorization/PermissionsGate";
import { SCOPES } from "common/permissions";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RequiredField from '../../common/RequiredField';
import intl from 'components/i18n/ReactIntlWrapper';
import { handleAPIError } from "common/errorHandler";


export type JobProjectInput = {
    description: string,
    billable: boolean,
    idProject: number,
    idJobType: number | null
};

type JobProjectProps = {
    idProject: number,
    isJiraReady: boolean,
    clientName?: string,
    projectName?: string,
    disabled: boolean,
    errors: any,
    setErrors: any
}


const JobProject = ({ idProject, isJiraReady, clientName, projectName, disabled, errors, setErrors }: JobProjectProps) => {
    const DefaultJobProjectInput = {
        description: '',
        billable: false,
        idProject: idProject,
        idJobType: null,
        currentPage: 1
    };

    const [input, setInput] = useState<CreateJobCommand | UpdateJobCommand | null>(DefaultJobProjectInput);
    const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
    const api: Api = useApi();

    const toast = useToastMessageQueue();

    const [taskJobEdit, setTaskJobEdit] = useState<number | null>(null);

    const [taskJobModalOpen, setTaskJobModalOpen] = useState(false);

    const [loading, setLoading] = useState<boolean>(false);

    const [jobsByProject, setJobsByProject] = useState([]);

    const [jobTypesList, setJobTypesList] = useState(null)

    const [limit, setLimit] = useState<number>(() => {
		return parseInt(localStorage.getItem('itemsPerPageJobProyect'), 10) || 10;
	});

    useEffect(() => {
		setLimit(parseInt(localStorage.getItem('itemsPerPageJobProyect'), 10) || 10);
	}, []);

     const fetchJobsByProject = async (idProject: number) => {
        setLoading(true);
        await api.jobsApi.apiVversionJobGetByProjectIdIdGet(idProject, "1", {}).then((response) => {
            if (response.data.data) {
                setJobsByProject(response.data.data.queryResult);
            };
            setLoading(false);
        }).catch((error) => {
            handleAPIError(error, toast, errors)
            setErrors({...errors})
            setLoading(false);
        });
    };

    const fetchJobsTypeList = async () => {
        setLoading(true);
        await api.jobsApi.apiVversionListJobtypesGet("1", {}).then((response) => {
            if (response.data && 'queryResult' in response.data) {
                setJobTypesList(response.data.queryResult);
            } 
            else if (response.data && 'data' in response.data && 'queryResult' in response.data.data) {
                setJobTypesList(response.data.data.queryResult);
            }
            setLoading(false);
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({...errors});
            setLoading(false);
        });
    };

    useEffect(() => {
        if (jobTypesList == null) {
            fetchJobsTypeList()
        };
    },[jobTypesList]);

    useEffect(() => {
        if (idProject) {
            fetchJobsByProject(idProject);
        };
    }, [idProject, limit]);

    const handleInputChange = (e: { target: { name: any; value: any; }; }) => {
        const inputVal = {
            ...input,
            [e.target.name]: e.target.value
        };
        setInput({ ...inputVal });
        validate({ ...inputVal });
    };

    const jobHasTimeLogs = async (idJob: number) => {
        return await api.timesheetApi.apiVversionTimesheetGetByJobIdJobGet(idJob, "1", 0, 0, {}).then(async response => {
            return response.data.data.totalRecords > 0;
        }
        ).catch((error) => {
            toast.error({ header: intl.get('jobProject.toast.error.jobHasTimeLogs'), message: error.message });
            return true;
        });
    };

    const { openDialog, closeDialog } = useDialog();

    const handleBillableChange = (e: { target: { name: any; checked: any; }; }) => {
        const checked = e.target.checked;
        if (isEditMode()) {
            jobHasTimeLogs((input as UpdateJobCommand).id).then(value => {
                if (value) {

                    openDialog({
                        title: intl.get('delete.modal.title'),
                        message: intl.get('confirm.modal.jobProject.handleBillableChange.message'),
                        type:'confirm',
                        confirmButton: {
                          confirmLabel: 'Accept',
                          handleConfirm: async () => {
                            setInput({
                                ...input,
                                billable: checked
                            })
                          }
                        },
                        cancelButton: {
                          cancelLabel: intl.get("delete.modal.cancel.button"),
                          handleCancel: () => {},
                        }
                    });
                } else {
                    setInput({
                        ...input,
                        billable: checked
                    });
                }
            });
        } else {
            setInput({
                ...input,
                billable: checked
            });
        }
    };

    const handleJobTypeChange = (idJobType) => {
        const inputVal = {
            ...input,
            idJobType: idJobType.id
        };
        setInput({ ...inputVal });
        validate({ ...inputVal });
    };
    const validate = (inputVal) => {
        let errors = {};
        if (!inputVal.description) {
            errors['description'] = ' is required';
        }
        if (!inputVal.idJobType) {
            errors['idJobType'] = ' is required';
        }
        setErrors({ ...errors });
        return Object.keys(errors).length > 0 ? errors : null;
    };

    const handleAddJobProject = async (e) => {
        e.preventDefault();
        setLoadingSubmit(true);
        try {
            const errors = validate(input);
            if (errors == null) {
                await api.jobsApi.apiVversionJobPost("1", input, {}).then((response) => {
                    toast.success({ message: intl.get('employeeProject.toast.success.handleAddJobProject')})
                    setInput(DefaultJobProjectInput);
                    fetchJobsByProject(idProject);
                });
            }
        } catch (error) {
            handleAPIError(error, toast, errors);
            setErrors({...errors});
        } finally {
            setLoadingSubmit(false);
        }
    };

    const handleSaveJobProject = async (e) => {
        e.preventDefault();
        setLoadingSubmit(true);
        try {
            const errors = validate(input);
            if (errors == null) {
                await api.jobsApi.apiVversionJobPut("1", input, {}).then((response) => {
                    toast.success({ message: intl.get('employeeProject.toast.success.handleSaveJobProject')})
                    setInput(DefaultJobProjectInput);
                    fetchJobsByProject(idProject);
                });
            }
        } catch (error) {
            handleAPIError(error, toast, errors);
            setErrors({...errors});
        } finally {
            setLoadingSubmit(false);
        }
    };

    const handleJobEdit = (idJob: number) => {
        if (!isEditMode()) {
            const editingJob = jobsByProject?.find((job: Job) => job.id == idJob);
            if (editingJob != null){
                handleJobTypeChange(idJob);  
                setInput({ ...editingJob, id: idJob }); 
            }
            else
                toast.error({ message: intl.get('employeeProject.toast.error.handleJobEdit')});
        } else {
            setInput(DefaultJobProjectInput);
        };
    };

    const handleJobDelete = (idJob: number) => {
        const dialogId = openDialog({
            title: intl.get('delete.modal.title'),
            message: intl.get('confirm.modal.jobProject.message'),
            type: 'confirm',
            confirmButton: {
              confirmLabel: intl.get("delete.modal.delete.button"),
              handleConfirm: async () => {
                try {
                  const response = await api.jobsApi.apiVversionJobIdDelete(idJob, "1", {});
                  if (response.status == 200) {
                    toast.success({ message: intl.get('employeeProject.toast.success.handleJobDelete') });
                    setJobsByProject((jobs) => [...jobs.filter(j => j.id != idJob)]);
                  } else {
                    toast.error({ message: intl.get('employeeProject.toast.error.handleJobDelete') });
                  }
                } catch (error) {
                  handleAPIError(error, toast, errors);
                  setErrors({ ...errors });
                } finally {
                  closeDialog(dialogId);
                }
              }
            },
            cancelButton: {
              cancelLabel: intl.get("delete.modal.cancel.button"),
              handleCancel: () => {
                closeDialog(dialogId);
              },
            }
        });
        return dialogId;
    };

    const handleTaskJob = (idJob: number) => {
        setTaskJobEdit(idJob);
        setTaskJobModalOpen(true);
    };

    const isEditMode = () => {
        return Object.keys(input).includes('id');
    };

    const handleCancelEdit = () => {
        setInput(DefaultJobProjectInput);
    };

    const isJobEditMode = (idJob: number) => {
        
        return isEditMode() && (input as UpdateJobCommand).id === idJob;
    };

    const getJobName = (idJob: number): string => {
        return jobsByProject.find(j => j.id == idJob).description;
    };

    const closeModal = () => {
        setTaskJobModalOpen(false);
        setTaskJobEdit(null);
    };

    return (
        <Box>
          <Typography variant="h5" component="h2" id="job" color="primary" sx={{ mb: 2 }}>
            {intl.get('jobProject.header')}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <PermissionsGate
              viewScopes={[SCOPES['projects.job.add'], SCOPES['projects.job.edit']]}
              editScopes={[SCOPES['projects.job.add'], SCOPES['projects.job.edit']]}
              viewRoles={[]}
              editRoles={[]}
            >
              <Grid container spacing={2}>
                <Grid
                    size={{
                        xs: 12,
                        md: 4
                    }}>
                  <TextField
                    fullWidth
                    id="description"
                    name="description"
                    label={intl.get('jobProject.body.description')}
                    placeholder={intl.get('jobProject.body.placeholder.description')}
                    value={disabled ? '' : input.description}
                    onChange={handleInputChange}
                    disabled={disabled}
                    error={!!errors.description}
                    helperText={errors.description}
                    inputProps={{ maxLength: 45 }}
                  />
                </Grid>
                <Grid
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    size={{
                        xs: 12,
                        md: 2
                    }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="billable"
                        name="billable"
                        onChange={handleBillableChange}
                        checked={disabled ? false : input.billable}
                        disabled={disabled}
                      />
                    }
                    label={intl.get('jobProject.body.billable')}
                  />
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        md: 3
                    }}>
                  <JobTypeSelect
                    handleJobTypeChange={handleJobTypeChange}
                    selectedJobType={input.idJobType}
                    jobTypes={jobTypesList}
                    disabled={disabled}
                    errors={errors}
                  />
                </Grid>
                <Grid
                    sx={{ display: 'flex', alignItems: 'center' }}
                    size={{
                        xs: 12,
                        md: 3
                    }}>
                  {!isEditMode() ? (
                    <PermissionsGate
                      viewScopes={[SCOPES['projects.job.add']]}
                      editScopes={[SCOPES['projects.job.add']]}
                      viewRoles={[]}
                      editRoles={[]}
                    >
                      {loadingSubmit ? (
                        <Box display={'flex'} alignItems={'center'} style={{ marginBottom: '20px' }}>
                          <Spinner small />
                        </Box>
                      ) : (
                      <Button
                        variant="contained"
                        disabled={disabled}
                        onClick={handleAddJobProject}
                        style={{ marginBottom: '20px' }}
                      >
                          {intl.get('projectDetail.add.button')}
                        </Button>
                      )}
                    </PermissionsGate>
                  ) : (
                    <PermissionsGate
                      viewScopes={[SCOPES['projects.job.edit']]}
                      editScopes={[SCOPES['projects.job.edit']]}
                      viewRoles={[]}
                      editRoles={[]}
                    >
                      {loadingSubmit ? (
                        <Box display={'flex'} alignItems={'center'} style={{ marginBottom: '20px' }}>
                          <Spinner small />
                        </Box>
                      ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={handleCancelEdit}
                          style={{ marginBottom: '20px' }}
                        >
                          {intl.get('projectDetail.cancel.button')}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSaveJobProject}
                          startIcon={<SaveOutlinedIcon />}
                        >
                          {intl.get('projectDetail.save.button')}
                        </Button>
                      </Box>
                      )}
                    </PermissionsGate>
                  )}
                </Grid>
                <Grid size={12}>
                  <RequiredField />
                </Grid>
              </Grid>
            </PermissionsGate>
          </Box>
          <Box>
            {loading ? (
              <Box display={'flex'} justifyContent={'center'} gap={1} my={2}>
                <Spinner small />
              </Box>
            ) : (
              <>
                <JobList
                  data={jobsByProject}
                  loading={loading} 
                  handleJobEdit={handleJobEdit}
                  handleTaskJob={handleTaskJob}
                  handleJobDelete={handleJobDelete}
                  disabled={disabled}
                />
              </>
            )}
          </Box>
          {taskJobModalOpen && taskJobEdit != null && (
            <TaskJobModal
              isOpen={taskJobModalOpen}
              closeModal={closeModal}
              idProject={idProject}
              idJob={taskJobEdit}
              JobName={getJobName(taskJobEdit)}
              ProjectName={projectName}
              ClientName={clientName}
              isJiraReady={isJiraReady}
            />
          )}
        </Box>
    );
}

export default JobProject;